import type { DoctorSubstituteDocumentApi } from '@omecare/utils';
import { useTranslation } from 'react-i18next';
import { DoctorSubstituteDocumentRow } from '@/components/doctor-substitute-documents/doctor-substitute-document-row';
import {
  listDoctorSubstituteDocuments,
  useDoctorSubstituteDocumentsViewModel,
} from '@/components/doctor-substitute-documents/use-doctor-substitute-documents.viewmodel';
import { FormSectionFullWidth } from '../../form-section';

/** Documents of the substitute being edited, shown inside the profile form. */
export type DoctorProfileFormDocuments = {
  doctorSubstituteId: string;
  licenceDocument: DoctorSubstituteDocumentApi;
  rcpDocument: DoctorSubstituteDocumentApi;
  canView: boolean;
  canEdit: boolean;
};

/**
 * Supporting-document rows rendered inside the "Profil contrat" section.
 * Gated by the LIVE thesis value so switching to non-thésé reveals the
 * licence row immediately. Uploads are saved on the spot, independently of
 * the surrounding form, which the hint under the rows says.
 *
 * @param props.documents - the substitute documents and the viewer's rights
 * @param props.thesis - the live thesis value of the form
 * @returns the rows and their hint
 */
export const ContractDocuments = ({
  documents,
  thesis,
}: {
  documents: DoctorProfileFormDocuments;
  thesis: boolean;
}) => {
  const { t } = useTranslation();
  const viewModel = useDoctorSubstituteDocumentsViewModel({
    doctorSubstituteId: documents.doctorSubstituteId,
  });
  const items = listDoctorSubstituteDocuments({
    thesis,
    licenceDocument: documents.licenceDocument,
    rcpDocument: documents.rcpDocument,
  });

  return (
    <FormSectionFullWidth className="grid gap-2">
      <span className="font-medium text-sm">
        {t('doctor-substitute-documents.title')}
      </span>
      <ul className="grid gap-2">
        {items.map((item) => (
          <DoctorSubstituteDocumentRow
            key={item.type}
            item={item}
            viewModel={viewModel}
            canView={documents.canView}
            canEdit={documents.canEdit}
          />
        ))}
      </ul>
      <p className="text-muted-foreground text-xs">
        {t('doctor-substitute-documents.form-hint')}
      </p>
    </FormSectionFullWidth>
  );
};
